import { APP_BASE_HREF } from "@angular/common";
import { Component, Inject, OnDestroy, OnInit, ChangeDetectorRef } from "@angular/core";
import { ActivatedRoute, NavigationEnd, Params, Router } from "@angular/router";
import { ProgressSpinnerService } from "@indusoft/progress-spinner";
import { ToastService, ToastSettings, ToastType } from "@indusoft/toast";
import { ReplaySubject, Subject, Subscription } from "rxjs";

import { ApplicationNames } from "../../../common/glossary/application-names";
import { RoutesGlossary } from "../../../common/glossary/routes-glossary";
import { ValidationLabelsGlossary } from "../../../common/glossary/validation-labels-glossary";
import { IndusoftUIInteractionService } from "../../../common/services/indusoft-ui-interaction-service";
import { AuthQueryParams } from "../glossary/auth-query-params-glossary";
import { AuthSteps } from "../models/auth-steps";
import { ConfigurationHttpService } from "../../../common/services/configuration.service";
import { AuthHttpService } from "../services/auth/auth-http.service";
import { AuthStore } from "../store/auth.store";
import { ThemeStore } from "../../../common/store/theme.store";
import { StringEncryptionService } from "../../../common/services/string-encryption.service";

import { GetAuthConfigResponse } from "../../../common/services/dtos/auth-config-get-response";

@Component({
    selector: 'auth',
    templateUrl: 'auth.component.html',
    styleUrls: ['auth.component.css']
})
export class AuthComponent implements OnInit, OnDestroy {
    private routerEventsSubscription: Subscription;
    private authConfig$: Subject<GetAuthConfigResponse> = new ReplaySubject(1);
    private destroy$ = new Subject<void>();

    public appName = ApplicationNames.APPLICATION_NAME;
    public showPassword: boolean = false;
    public domainAuthSelected: boolean = false;
    public autoLoginEnabled: boolean = false;
    public providerName: string | undefined = undefined;
    public showSwitch: boolean = false;
    public AuthSteps = AuthSteps;
    public ValidationLabelsGlossary = ValidationLabelsGlossary;
    public continueText: string = 'Войти';
    public continueWithProviderText: string | undefined = undefined;
    public continueWithProviderEnabled: boolean = false;

    constructor(
        @Inject(APP_BASE_HREF) public baseHref: string,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        public themeStore: ThemeStore,
        public authStore: AuthStore,
        private toastService: ToastService,
        private progressSpinnerService: ProgressSpinnerService,
        private authHttpService: AuthHttpService,
        private indusoftUIInteractionService: IndusoftUIInteractionService,
        private configurationHttpService: ConfigurationHttpService) {
    }

    public ngOnInit(): void {
        this.indusoftUIInteractionService.initialize();
        this.interactWithIndusoftUI();
        document.addEventListener(IndusoftUIInteractionService.CHECK_SHOW_SWITCH_EVENT_NAME, this.showSwitchEventHandler);

        this.loadAuthConfig();
        this.applyAuthConfig();

        this.routerEventsSubscription = this.router.events.subscribe(
            event => {
                if (event instanceof NavigationEnd) {

                    const queryParamStep = this.activatedRoute.snapshot.queryParams[AuthQueryParams.STEP_QUERY_PARAM];

                    if (!queryParamStep) {
                        const ignoreDomainUserAutoLoginParam = !!this.activatedRoute.snapshot.queryParams[AuthQueryParams.IGNORE_DOMAIN_AUTOLOGIN];

                        const returnUrlParam = this.activatedRoute.snapshot.queryParams[AuthQueryParams.RETURN_URL_PARAM];
                        if (!!returnUrlParam) {
                            this.authStore.setReturnUrl(returnUrlParam);
                        }

                        if (ignoreDomainUserAutoLoginParam) {
                            this.authStore.authStep = AuthSteps.LOGIN_PASSWORD_STEP;
                            return;
                        } else {
                            this.checkAutoLoginDomain()
                            return;
                        }
                    }

                    const authStep = Number(queryParamStep);

                    if (Number.isNaN(authStep) || authStep === AuthSteps.AUTO_LOGIN_DOMAIN_STEP) {
                        this.checkAutoLoginDomain()
                        return;
                    }

                    if (authStep === AuthSteps.FORBIDDEN_STEP || authStep === AuthSteps.ERROR_STEP) {
                        const message = authStep === AuthSteps.FORBIDDEN_STEP
                            ? "У Вас недостаточно прав для входа в систему"
                            : "Внутренняя ошибка";
                        this.authStore.authStep = AuthSteps.LOGIN_PASSWORD_STEP;
                        this.toastService.show(new ToastSettings(ToastType.Error, "Ошибка", message));
                    }
                }
            }
        );
    }

    public ngOnDestroy(): void {
        this.themeStore.destroy();

        document.removeEventListener(IndusoftUIInteractionService.CHECK_SHOW_SWITCH_EVENT_NAME, this.showSwitchEventHandler);

        if (this.routerEventsSubscription)
            this.routerEventsSubscription.unsubscribe();

        this.destroy$.next();
        this.destroy$.complete();
    }

    public checkAndLogin(): void {
        this.authStore.setIsShouldBeValidated(true);

        if (this.authStore.isValid === false) {
            this.toastService.show(new ToastSettings(ToastType.Error, "Ошибка", "Не заполнены обязательные поля."));
            return;
        }

        this.domainAuthSelected = false;
        this.loginJail();
    }

    public tryDomainAndLogin(): void {
        this.router.navigate(
            [`/${RoutesGlossary.LoginDomain}`],
            {
                queryParams: {
                    step: <number>AuthSteps.LOGIN_PASSWORD_STEP
                }
            })
            .then(
                () => {
                    let returnUrlValue = window.location.pathname + window.location.search;

                    if (!!this.authStore.returnUrl) {
                        const separator = returnUrlValue.indexOf("?") > 0 ? "&" : "?";
                        returnUrlValue += `${separator}${AuthQueryParams.RETURN_URL_PARAM}=${this.authStore.returnUrl}`
                    }

                    this.router.navigate(
                        [`/${RoutesGlossary.GetKrbUser}`],
                        {
                            queryParams: {
                                returnUrl: returnUrlValue,
                                step: <number>AuthSteps.LOGIN_PASSWORD_STEP
                            }
                        })
                        .then(
                            () => {
                                this.domainAuthSelected = true;
                                window.location.reload();
                            }
                        );
                }
            );
    }

    public fieldChanged(): void {
        this.authStore.setIsShouldBeValidated(false);
    }

    public toggleVisible(): void {
        this.indusoftUIInteractionService.isVisible = !this.indusoftUIInteractionService.isVisible;
    }

    public get tooltipUIPanelButton(): string {
        return this.indusoftUIInteractionService.isVisible === true ? "Скрыть панель" : "Показать панель";
    }

    private setStep(step: AuthSteps): void {
        this.authStore.authStep = step;

        this.router.navigate(
            [],
            {
                queryParams: this.buildUrlParams()
            }
        );
    }

    private buildUrlParams(): Params {
        const params: Params = [];
        params[AuthQueryParams.STEP_QUERY_PARAM] = <number>this.authStore.authStep;

        if (!!this.authStore.returnUrl) {
            params[AuthQueryParams.RETURN_URL_PARAM] = this.authStore.returnUrl;
        }

        return params
    }

    private checkAutoLoginDomain() {

        this.authConfig$
            .subscribe(response => {
                if (response.autoLoginEnabled) {
                    this.tryDomainAndLogin();
                } else {
                    this.setStep(AuthSteps.LOGIN_PASSWORD_STEP);
                }
            });
    }

    private loginJail(): void {
        if (this.domainAuthSelected)
            this.loginUserDomain();
        else
            this.loginUser();
    }

    private loginUser(): void {
        const userName = this.authStore.login;
        const passwordEncrypted = StringEncryptionService.encryptString(
            this.authStore.password,
            AuthQueryParams.AUTH_KEY);
        const rememberMe = this.authStore.rememberMe;

        const spinner = this.progressSpinnerService.show({ title: "Аутентификация..." });
        this.authHttpService
            .logIn(userName, passwordEncrypted, rememberMe)
            .subscribe(
                result => {
                    this.progressSpinnerService.remove(spinner.id);
                    if (!result) {
                        this.toastService.show(new ToastSettings(ToastType.Error, "Ошибка", "Произошла ошибка при аутентификации"));
                        return;
                    }

                    if (result.isSuccess === false) {
                        this.toastService.show(new ToastSettings(ToastType.Error, "Ошибка", `Произошла ошибка при аутентификации: ${result.message}`));
                        return;
                    }

                    this.goToReturnUrlOrRoot();
                }
            );
    }

    private loginUserDomain(): void {
        let url = `${RoutesGlossary.LoginDomain}`;

        if (!!this.authStore.returnUrl) {
            url += `?${AuthQueryParams.RETURN_URL_PARAM}=${encodeURIComponent(this.authStore.returnUrl)}`;
        }

        window.location.href = `${url}`;
    }

    private goToReturnUrlOrRoot(): void {

        let url = this.baseHref;

        if (!!this.authStore.returnUrl) {
            url = window.location.origin + this.authStore.returnUrl;
        }

        window.open(url, RoutesGlossary.Same);
    }

    // Обработка событий из IndusoftUI
    private showSwitchEventHandler = () => {
        this.interactWithIndusoftUI();
    }

    private interactWithIndusoftUI(): void {
        this.showSwitch = this.indusoftUIInteractionService.isShowSwitch;
    }

    private loadAuthConfig(): void {
        this.configurationHttpService.getAuthConfiguration()
            .subscribe(authConfig => {
                if (!authConfig) {
                    this.authConfig$.next({
                        autoLoginEnabled: false,
                        providerName: ""
                    });
                    return;
                }

                this.authConfig$.next(authConfig);
            });
    }

    private applyAuthConfig(): void {
        this.authConfig$
            .subscribe(response => {
                this.autoLoginEnabled = response.autoLoginEnabled;
                this.providerName = response.providerName;

                if (this.providerName) {

                    if (this.providerName.trim().toLowerCase() === 'keycloak') {
                        this.continueWithProviderEnabled = false;
                        this.continueText = 'Войти с учетной записью Keycloak';
                    }
                    else {
                        this.continueWithProviderEnabled = true;
                    }
                    this.continueWithProviderText = `Войти с учетной записью ${this.providerName}`;

                } else {
                    this.continueWithProviderEnabled = false;
                    this.continueWithProviderText = undefined;
                }
            });
    }
}